import { supabaseUrl } from '@/lib/supabase'
import { createVozillaRelease, type DownloadItem, type VozillaRelease } from './release-assets'

const RELEASE_DISTRIBUTION_BASE_URL = `${supabaseUrl.replace(/\/+$/, '')}/functions/v1/release-distribution`

type ReleaseManifestJson = {
  created_at?: unknown
  products?: {
    vozilla?: {
      version?: unknown
    }
  }
}

function parseManifest(data: unknown): { version: string; publishedAt: string | null } {
  if (!data || typeof data !== 'object') throw new Error('Release service returned an invalid manifest')
  const manifest = data as ReleaseManifestJson
  const version = manifest.products?.vozilla?.version
  if (typeof version !== 'string' || version.length === 0) {
    throw new Error('The latest Vozilla release is not present in the release manifest')
  }
  return {
    version,
    publishedAt: typeof manifest.created_at === 'string' ? manifest.created_at : null,
  }
}

export async function fetchLatestVozillaRelease(accessToken: string): Promise<VozillaRelease> {
  const response = await fetch(`${RELEASE_DISTRIBUTION_BASE_URL}/manifests/main/latest.yaml`, {
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    if (response.status === 401) throw new Error('Sign in again to access Vozilla releases')
    if (response.status === 403) throw new Error('An active license is required to download Vozilla')
    throw new Error(`Unable to load Vozilla releases (HTTP ${response.status})`)
  }

  const { version, publishedAt } = parseManifest(await response.json())
  return createVozillaRelease(version, publishedAt, RELEASE_DISTRIBUTION_BASE_URL)
}

/** Exchange the current session for a short-lived, download-only S3 URL. */
export async function downloadVozillaAsset(item: DownloadItem, accessToken: string): Promise<void> {
  const response = await fetch(item.url, {
    cache: 'no-store',
    headers: {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  })
  if (!response.ok) {
    if (response.status === 401) throw new Error('Sign in again to download Vozilla')
    if (response.status === 403) throw new Error('An active license is required to download Vozilla')
    throw new Error(`Download failed (HTTP ${response.status})`)
  }

  const body = (await response.json()) as { url?: unknown }
  if (typeof body.url !== 'string') throw new Error('Release service returned an invalid download URL')
  const signedUrl = new URL(body.url)
  if (signedUrl.protocol !== 'https:' || !signedUrl.hostname.endsWith('.amazonaws.com')) {
    throw new Error('Release service returned an invalid download URL')
  }

  const link = document.createElement('a')
  link.href = signedUrl.href
  link.download = item.filename
  document.body.appendChild(link)
  link.click()
  link.remove()
}
